import React, { useState } from 'react';
import { PageHeader } from '../../components/ui';
import { Overview } from './components/Overview';
import { VendorRecon } from './components/VendorRecon';
import { BankBridge } from './components/BankBridge';
import { UnappliedPayments } from './components/UnappliedPayments';
import { Findings } from './components/Findings';
import { GstReconciliation } from './components/GstReconciliation';
import { StatutoryRecon } from './components/StatutoryRecon';

/**
 * Financial Reconciliation dashboard.
 *
 * Read-only lens over Zoho (system of record) + email + bank + the reimbursement
 * sheet, for Apr 2025 → today. Tabs, not routes, keep the whole audit on one screen
 * for a showcase; each can be promoted to its own route later.
 */
/**
 * `hidden: true` takes a tab out of the tab bar without touching its component, its data
 * fetching, or its route. Vendor Reconciliation is the only finished view; the rest are
 * built but not ready to show, so they are hidden rather than deleted or commented out —
 * flip the flag to bring one back.
 */
const TABS = [
	{ id: 'overview', label: 'Overview', hidden: true },
	{ id: 'vendors', label: 'Vendor Reconciliation' },
	{ id: 'gst', label: 'GST Reconciliation' },
	{ id: 'statutory', label: 'PF · ESIC · PT' },
	{ id: 'bank', label: 'Bank & Razorpay', hidden: true },
	{ id: 'unapplied', label: 'Unapplied', hidden: true },
	{ id: 'findings', label: 'Findings', hidden: true },
];
const VISIBLE_TABS = TABS.filter(t => !t.hidden);
const shown = (id: string) => VISIBLE_TABS.some(t => t.id === id);

export const ReconciliationDashboard: React.FC = () => {
	const [tab, setTab] = useState(VISIBLE_TABS[0].id);

	return (
		<div className='p-6 max-w-7xl mx-auto'>
			<PageHeader
				title='Financial Reconciliation'
				totalItems={0}
				itemType='audit'
				icon='🔍'
			/>

			<div className='flex gap-1 border-b border-gray-200 mb-6'>
				{VISIBLE_TABS.map(t => (
					<button
						key={t.id}
						onClick={() => setTab(t.id)}
						className={`px-4 py-2.5 text-sm font-medium border-b-2 transition -mb-px ${
							tab === t.id
								? 'border-blue-600 text-blue-600'
								: 'border-transparent text-gray-500 hover:text-gray-700'
						}`}>
						{t.label}
					</button>
				))}
			</div>

			{/* Every VISIBLE tab stays mounted and toggles visibility rather than being
			    conditionally rendered — unmounting would reset its own filter state (date
			    range, bucket, search) on every switch. Hidden tabs are not rendered at all:
			    mounting one still fires its API calls, and this page was making ~8 requests
			    for tabs nobody could see. */}
			{shown('overview') && (
				<div className={tab === 'overview' ? '' : 'hidden'}>
					<Overview />
				</div>
			)}
			<div className={tab === 'vendors' ? '' : 'hidden'}>
				<VendorRecon />
			</div>
			<div className={tab === 'gst' ? '' : 'hidden'}>
				<GstReconciliation />
			</div>
			{/* Mounted only once opened. The other two tabs fetch on mount, and this one would add
			    two more requests to a page load nobody asked for. */}
			{tab === 'statutory' && (
				<div>
					<StatutoryRecon />
				</div>
			)}
			{shown('bank') && (
				<div className={tab === 'bank' ? '' : 'hidden'}>
					<BankBridge />
				</div>
			)}
			{shown('unapplied') && (
				<div className={tab === 'unapplied' ? '' : 'hidden'}>
					<UnappliedPayments />
				</div>
			)}
			{shown('findings') && (
				<div className={tab === 'findings' ? '' : 'hidden'}>
					<Findings />
				</div>
			)}
		</div>
	);
};

export default ReconciliationDashboard;
